import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { FloorplanTransform } from '@/types';

interface BrandingCanvasHandlesProps {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  transform: FloorplanTransform;
  imageNaturalSize: { width: number; height: number } | null;
  frame: { width: number; height: number };
  onDragBody: (deltaNormalizedX: number, deltaNormalizedY: number) => void;
  onCornerScale: (args: { scaleFactor: number; startScale: number }) => void;
  onRotate: (args: { angleDeg: number }) => void;
  /** When false, no handles or selection outline are rendered (overlay is invisible/non-blocking). */
  isSelected: boolean;
  /** Called when the user clicks the image body to select the grundriss. */
  onSelect: () => void;
}

/**
 * Canva-style direct-manipulation overlay for the floorplan canvas.
 * - 4 corner-handles: uniform scale (aspect-ratio preserving)
 * - Rotate-handle below bottom-center: rotation around canvas center
 * - Body-drag: pan/translate
 *
 * Positioned absolutely to match the canvas' current bounding box in the viewport.
 * Auto-repositions on resize (ResizeObserver + window resize).
 *
 * Selection model: handles + outline only render when `isSelected === true`.
 * When deselected, returns null so the underlying canvas is fully clickable
 * — selection is then driven by the canvas-level pointer handler in the parent.
 */
export const BrandingCanvasHandles: React.FC<BrandingCanvasHandlesProps> = ({
  canvasRef,
  transform,
  imageNaturalSize,
  frame,
  onDragBody,
  onCornerScale,
  onRotate,
  isSelected,
  onSelect,
}) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [visibleRect, setVisibleRect] = useState<DOMRect | null>(null);

  // Track canvas position/size changes (object-contain letterboxing, parent resize, etc.)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const updateRect = () => setVisibleRect(canvas.getBoundingClientRect());
    updateRect();

    const resizeObserver = new ResizeObserver(updateRect);
    resizeObserver.observe(canvas);
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [canvasRef]);

  const handleCornerPointerDown = useCallback(
    (_handleId: 'nw' | 'ne' | 'sw' | 'se') => (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!visibleRect) return;

      // Image center in screen coords — distance-from-center approach is rotation-invariant.
      const centerX = visibleRect.left + visibleRect.width * transform.tx;
      const centerY = visibleRect.top + visibleRect.height * transform.ty;
      const startDist = Math.hypot(e.clientX - centerX, e.clientY - centerY);
      const startScale = transform.scale;

      const onMove = (ev: PointerEvent) => {
        const currentDist = Math.hypot(ev.clientX - centerX, ev.clientY - centerY);
        const scaleFactor = currentDist / Math.max(1, startDist);
        onCornerScale({ scaleFactor, startScale });
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [transform.scale, transform.tx, transform.ty, onCornerScale, visibleRect]
  );

  const handleRotatePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!visibleRect || !imageNaturalSize) return;
      // Rotation center = image center in screen coords (not canvas center)
      const centerX = visibleRect.left + visibleRect.width * transform.tx;
      const centerY = visibleRect.top + visibleRect.height * transform.ty;

      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - centerX;
        const dy = ev.clientY - centerY;
        // atan2 returns angle where 0 = +X axis. Rotate -90 so that the handle (below center)
        // at its default position corresponds to rotation = 0.
        const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI - 90;
        onRotate({ angleDeg });
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [onRotate, visibleRect, imageNaturalSize, transform.tx, transform.ty]
  );

  const handleBodyPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Stop propagation so the canvas-background click in the parent doesn't
      // immediately deselect us when the user starts a drag on the body border.
      e.stopPropagation();
      onSelect();
      if (!visibleRect) return;
      const startX = e.clientX;
      const startY = e.clientY;

      const onMove = (ev: PointerEvent) => {
        // tx/ty are frame-relative 0..1; divide by canvas DOM size to normalize
        const dx = (ev.clientX - startX) / visibleRect.width;
        const dy = (ev.clientY - startY) / visibleRect.height;
        onDragBody(dx, dy);
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [onDragBody, onSelect, visibleRect]
  );

  // When not selected, render nothing — the underlying canvas handles its own
  // clicks/drags via useFloorplanTransform's onPointerDown, and the parent
  // CanvasPane wires that pointer-down to also call onSelect().
  if (!isSelected) return null;
  if (!visibleRect || !imageNaturalSize) return null;

  // Map the visual (pre-rotation) image rect from frame-coords → canvas-DOM coords.
  // We size the wrapper to the SCALED IMAGE dimensions (not the AABB), then apply
  // CSS rotation so corner handles land exactly on the visual image corners.
  const scaleX = visibleRect.width / frame.width;
  const scaleY = visibleRect.height / frame.height;

  const visualW = imageNaturalSize.width * transform.scale * scaleX;
  const visualH = imageNaturalSize.height * transform.scale * scaleY;

  const centerX = visibleRect.left + frame.width * transform.tx * scaleX;
  const centerY = visibleRect.top + frame.height * transform.ty * scaleY;

  const boxLeft = centerX - visualW / 2;
  const boxTop = centerY - visualH / 2;

  const HANDLE_SIZE = 14;
  const ROTATE_SIZE = 24;
  const ROTATE_STEM = 18;

  return (
    <div
      ref={overlayRef}
      className="fixed pointer-events-none"
      style={{
        left: boxLeft,
        top: boxTop,
        width: visualW,
        height: visualH,
        transform: `rotate(${transform.rotation}deg)`,
        transformOrigin: 'center',
      }}
    >
      {/* Selection outline + body drag */}
      <div
        className="absolute inset-0 border-[1.5px] border-primary cursor-move pointer-events-auto"
        onPointerDown={handleBodyPointerDown}
      />

      {/* 4 Corner handles */}
      {(['nw', 'ne', 'sw', 'se'] as const).map((id) => {
        const isTop = id.startsWith('n');
        const isLeft = id.endsWith('w');
        const cursor = id === 'nw' || id === 'se' ? 'nwse-resize' : 'nesw-resize';
        return (
          <div
            key={id}
            className="absolute bg-white border-2 border-primary rounded-full shadow-sm pointer-events-auto"
            style={{
              width: HANDLE_SIZE,
              height: HANDLE_SIZE,
              [isTop ? 'top' : 'bottom']: -HANDLE_SIZE / 2,
              [isLeft ? 'left' : 'right']: -HANDLE_SIZE / 2,
              cursor,
            }}
            onPointerDown={handleCornerPointerDown(id)}
          />
        );
      })}

      {/* Rotate stem + handle, below bottom-center */}
      <div
        className="absolute left-1/2 flex flex-col items-center pointer-events-none"
        style={{
          bottom: -(ROTATE_STEM + ROTATE_SIZE + 4),
          transform: 'translateX(-50%)',
        }}
      >
        <div className="w-px bg-primary/50" style={{ height: ROTATE_STEM }} />
        <div
          className="bg-white border-2 border-primary rounded-full shadow-md flex items-center justify-center text-primary pointer-events-auto"
          style={{ width: ROTATE_SIZE, height: ROTATE_SIZE, cursor: 'grab' }}
          onPointerDown={handleRotatePointerDown}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 12a9 9 0 1 0 3-6.7" />
            <polyline points="3 4 3 9 8 9" />
          </svg>
        </div>
      </div>
    </div>
  );
};
